import { useMemo, useState } from "react";
import { SendIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  type DocumentSuggestion,
  useListCfrTemplates,
} from "@/lib/api/cfr";
import {
  SuggestionSection,
  type CfrDocumentTemplateRow,
  type CreateInstanceDialogOptions,
  generateDefaultEmptyInstanceInfo,
} from "../List";
import { UploadSuggestionsReportDialog } from "../components/UploadSuggestionsReportDialog";
import { CreateInstanceDialog } from "../components/CreateInstanceDialog";

export function CfrReportsPage() {
  const listTemplateResult = useListCfrTemplates();
  const templateData = useMemo<CfrDocumentTemplateRow[]>(() => {
    return listTemplateResult?.data?.data?.data || [];
  }, [listTemplateResult]);

  const [createInstanceInfo, setCreateInstanceInfo] =
    useState<CreateInstanceDialogOptions>(generateDefaultEmptyInstanceInfo());

  const handleSuggestionClick = (row: DocumentSuggestion) => {
    const relevantPotentialTemplate = templateData.find(
      (item) =>
        item.crop_year === row.crop_year &&
        item.commodity_type === row.commodity &&
        item.customer_destination_id === row.destination_id,
    );

    setCreateInstanceInfo({
      suggestionId: row.id || "",
      selectedCustomerId: row.customer_id || "",
      selectedTemplateId: relevantPotentialTemplate?.id || "",
      isDialogOpen: true,

      selectedMarketZoneId: row.market_zone_id || "",
      selectedDestinationId: row.destination_id || "",

      commodity: row.commodity || "",
      contractNumber: row.contract_number || "",
      contractQuantity: row.contract_quantity || "",

      contract_shipping_dates: row.original_meta?.shipment_range || "",
      contractType: row.original_meta?.contract_type || "",
      documentTitle: row.original_meta?.document_title,
    });
  };

  const handleBlankSend = () => {
    setCreateInstanceInfo({
      ...generateDefaultEmptyInstanceInfo(),
      isDialogOpen: true,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-wide">
            CFR
          </p>
          <h2 className="text-2xl font-bold">Reports & Suggestions</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleBlankSend}>
            <SendIcon className="mr-2 h-4 w-4" />
            Send Template
          </Button>
          <UploadSuggestionsReportDialog />
        </div>
      </div>

      <SuggestionSection
        onSuggestionClick={handleSuggestionClick}
        templateData={templateData}
      />

      {createInstanceInfo.isDialogOpen && (
        <CreateInstanceDialog
          open={createInstanceInfo.isDialogOpen}
          onOpenChange={(status: boolean) => {
            setCreateInstanceInfo((prev) => ({
              ...prev,
              isDialogOpen: status,
            }));
          }}
          onSuccess={() => {}}
          initialData={{
            customerId: createInstanceInfo.selectedCustomerId,
            templateId: createInstanceInfo.selectedTemplateId,
            suggestionId: createInstanceInfo.suggestionId,
            contractNumber: createInstanceInfo.contractNumber,
            contractType: createInstanceInfo.contractType,
            contractQuantity: createInstanceInfo.contractQuantity,
            documentTitle: createInstanceInfo.documentTitle,
            contract_shipping_dates: createInstanceInfo.contract_shipping_dates,
          }}
        />
      )}
    </div>
  );
}

